import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs';
import { TutorialService } from '../../services/tutorial/tutorial.service';

@Component({
  selector: 'bom-fixed-menu',
  templateUrl: './fixed-menu.component.html',
  styleUrls: ['./fixed-menu.component.scss'],
})
export class FixedMenuComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('menu', { static: true })
  menuElement: ElementRef;

  header: HTMLElement;

  headerContentSpacing = 24;

  scrollIntoViewLastElementToShow: any;
  scrollIntoViewInterval: NodeJS.Timer;

  isScrollbarLocked$: Observable<boolean>;
  isSticky = false;

  get menu(): HTMLElement {
    return this.menuElement.nativeElement;
  }

  constructor(private zone: NgZone, private tutorialService: TutorialService) {}

  ngOnInit(): void {
    this.isScrollbarLocked$ = this.tutorialService.isMenuLocked$;
  }

  ngAfterViewInit() {
    this.initializeLayout();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', e => this.updateMenu());
    clearInterval(this.scrollIntoViewInterval);
  }

  private initializeLayout() {
    // Find the header dom element
    this.header = document.querySelector('.header');

    if (!this.header) {
      return;
    }

    // Set initial menu height
    // We need to use setTimeout, otherwise the header will appear to be 40px higher
    setTimeout(() => this.updateMenu(), 0);

    // Update the menu height when the user scroll
    // NgZone and a zero timeout is used to improve scroll performance in IE specifically
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', e => setTimeout(() => this.updateMenu(), 0));
    });

    this.initializeScrollIntoView();
  }

  private updateMenu() {
    const windowScrollY = this.getWindowScrollY();
    if (windowScrollY > this.header.offsetHeight) {
      // Header is scrolled out of view - fix position the menu
      if (!this.isSticky) {
        this.menu.style.height = this.calculateStickyHeight();
        this.isSticky = true;
      }
    } else {
      // Header is within view - remove possibly added fixed class
      this.menu.style.height = this.calculateHeight(windowScrollY);
      this.isSticky = false;
    }
  }

  private initializeScrollIntoView() {
    this.scrollIntoViewInterval = setInterval(() => {
      const activeElements = this.menu.querySelectorAll('.active');

      if (!activeElements || activeElements.length === 0) {
        return;
      }

      const visibleElements = Array.from(activeElements).filter((el: any) => el.offsetHeight > 0);

      // There might be multiple active items (eg. in case of a nested menu).
      // Therefore we take the last element found
      // If it's another element than the last element we found scroll it into view if needed
      const elementToShow = visibleElements[visibleElements.length - 1];
      if (this.scrollIntoViewLastElementToShow !== elementToShow) {
        this.scrollIntoViewLastElementToShow = elementToShow;
        this.scrollIntoViewIfNeeded(elementToShow);
      }
    }, 300);
  }

  private calculateHeight(windowScrollY: number): string {
    const heightOffset = this.header.offsetHeight + this.headerContentSpacing * 2 - windowScrollY;
    return 'calc(100vh - ' + heightOffset + 'px)';
  }

  private calculateStickyHeight(): string {
    const heightOffset = this.headerContentSpacing * 2;
    return 'calc(100vh - ' + heightOffset + 'px)';
  }

  private getWindowScrollY() {
    const doc = document.documentElement;
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    return top;
  }

  private scrollIntoViewIfNeeded(element: any) {
    if (!this.menu || !element) {
      return;
    }

    // Calculate the element offset in relation to the menu
    let elementMenuOffset = 0;
    let offsetElement = element;
    while (offsetElement && offsetElement !== this.menu && offsetElement !== document.body) {
      elementMenuOffset = elementMenuOffset + offsetElement.offsetTop;
      offsetElement = offsetElement.offsetParent;
    }

    // Determine the visible scroll span to check if the element is already in view
    const visibleScrollAreaMin = this.menu.scrollTop;
    const visibleScrollAreaMax = this.menu.scrollTop + this.menu.offsetHeight;

    if (
      visibleScrollAreaMin <= elementMenuOffset &&
      elementMenuOffset + element.offsetHeight <= visibleScrollAreaMax
    ) {
      // Not scrolling. already in view
      return;
    } else {
      if (elementMenuOffset < visibleScrollAreaMin) {
        // Scroll up
        this.menu.scrollTop = elementMenuOffset;
      } else {
        // Scroll down
        this.menu.scrollTop = elementMenuOffset + element.offsetHeight - this.menu.offsetHeight;
      }
    }
  }
}
